import React from 'react';
import { DroneTargetInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { isNonEmpty } from '@ais-ui/utils';
import { Separator } from '@ais-ui/ui-core';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { LastOriginDisplay } from './LastOriginDisplay';

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

interface IdentificationProps {
  droneInfo: DroneTargetInfo;
}

const Identification = ({ droneInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <IdentificationItem
        label={messages.reportsDetailsTypeLabel}
        value={droneInfo.targetType}
      />
      <IdentificationItem
        label="MAC Address"
        value={droneInfo.macAddress}
      />
      <IdentificationItem
        label="UAS ID"
        value={droneInfo.uasId}
      />
      <IdentificationItem
        label="UA Type"
        value={droneInfo.uaType}
      />
      <IdentificationItem
        label="Message Type"
        value={droneInfo.messageType}
      />
      <IdentificationItem
        label="Scanner IMEI"
        value={droneInfo.imeiScanner}
      />
    </div>
  );
};

interface FlightDataProps {
  droneInfo: DroneTargetInfo;
}

interface FlightDataItemProps {
  label: string;
  value: string;
}

const FlightDataItem = ({ label, value }: FlightDataItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const FlightData = ({ droneInfo }: FlightDataProps) => {
  return (
    <div className="grid grid-cols-2 gap-1">
      <FlightDataItem label="Altitude" value={droneInfo.altitude} />
      <FlightDataItem label="Speed" value={droneInfo.speed} />
      <FlightDataItem label="Heading" value={droneInfo.heading} />
    </div>
  );
};

interface PositionReportingProps {
  droneInfo: DroneTargetInfo;
}

interface DronePositionItemProps {
  label: string;
  latitude: string;
  longitude: string;
}

const DronePositionItem = ({
  label,
  latitude,
  longitude,
}: DronePositionItemProps) => {
  const hasValidCoordinates = isNonEmpty(latitude) && isNonEmpty(longitude);

  return (
    <div className="col-span-2">
      <div className={'flex flex-col mt-1'}>
        <span className="text-muted-foreground text-xs">{label}</span>
        <span className="text-xs whitespace-nowrap select-text">
          {hasValidCoordinates ? `${latitude}, ${longitude}` : '-'}
        </span>
      </div>
    </div>
  );
};

interface PositionReportingItemProps {
  label: string;
  value: string;
}

const PositionReportingItem = ({
  label,
  value,
}: PositionReportingItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const PositionReporting = ({ droneInfo }: PositionReportingProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <PositionReportingItem
        label="Last Report Timestamp"
        value={formatDateTimeWithTimeZone(droneInfo.lastReportTimestamp)}
      />
      <DronePositionItem
        label="Drone Position"
        latitude={droneInfo.latitudeDegrees}
        longitude={droneInfo.longitudeDegrees}
      />
      <DronePositionItem
        label="Operator Position"
        latitude={droneInfo.operatorLatitude}
        longitude={droneInfo.operatorLongitude}
      />
    </div>
  );
};

interface OperationalDataProps {
  droneInfo: DroneTargetInfo;
}

interface OperationalDataItemProps {
  label: string;
  value: string;
}

const OperationalDataItem = ({
  label,
  value,
}: OperationalDataItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

const OperationalData = ({ droneInfo }: OperationalDataProps) => {
  return (
    <div className="grid grid-cols-2 gap-1">
      <OperationalDataItem
        label="Operator ID Type"
        value={droneInfo.operatorIdType}
      />
      <OperationalDataItem
        label="Operator Location Type"
        value={droneInfo.operatorLocationType}
      />
      <OperationalDataItem
        label="Classification Type"
        value={droneInfo.classificationType}
      />
      <OperationalDataItem
        label="EU Class"
        value={droneInfo.classEu}
      />
      <OperationalDataItem
        label="Operation Status"
        value={droneInfo.operationStatus}
      />
      <OperationalDataItem
        label="Operation Category EU"
        value={droneInfo.operationCategoryEu}
      />
    </div>
  );
};

export const DroneDetailsPanel = ({
  droneInfo,
}: {
  droneInfo: DroneTargetInfo;
}) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification droneInfo={droneInfo}></Identification>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Flight Data</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <FlightData droneInfo={droneInfo}></FlightData>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Position & Reporting</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <PositionReporting droneInfo={droneInfo}></PositionReporting>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Operational & Regulatory Data</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <OperationalData droneInfo={droneInfo}></OperationalData>
      </div>

      {droneInfo.lastOrigin && (
        <LastOriginDisplay lastOrigin={droneInfo.lastOrigin} />
      )}
    </div>
  );
};
